<template>
  <div
    v-if="isLoginDrawerOpen"
    class="fixed inset-0 z-10 h-screen w-screen overflow-hidden bg-neutral-500/50 transition-opacity"
    @click="toggleLoginDrawer"
  />
  <transition
    enter-active-class="transition duration-500 ease-in-out"
    leave-active-class="transition duration-500 ease-in-out"
    enter-from-class="translate-x-full"
    enter-to-class="translate-x-0"
    leave-from-class="translate-x-0"
    leave-to-class="translate-x-full"
  >
    <SfDrawer
      v-model="isLoginDrawerOpen"
      placement="right"
      class="z-20 flex w-[500px] flex-col bg-white p-0 mobile:w-full"
    >
      <header
        v-if="viewType === 'registration'"
        class="flex items-center justify-between border-b p-4"
      >
        <h3 class="text-neutral-700">
          {{ $t('Register') }}
        </h3>
        <KikButton
          square
          variant="tertiary"
          :aria-label="$t('Close')"
          @click="toggleLoginDrawer"
        >
          <SfIconClose class="fill-neutral-500 text-neutral-500" />
        </KikButton>
      </header>
      <div
        class="h-full overflow-y-auto py-0"
      >
        <template v-if="viewType === 'default'">
          <section class="mb-1 bg-neutral-50 p-4 pb-7">
            <div class="relative mb-4 border-4 border-primary-700 p-4">
              <KikHeading
                :level="3"
                title="Welcome back"
                description="Log in to start shopping right away"
                class="mb-6 text-center"
              >
                <template #description="{ description }">
                  <div class="mb-3 text-xs font-light leading-4 text-neutral-500">
                    {{ $t(description) }}
                  </div>
                </template>
              </KikHeading>
              <LegacyAuth v-if="$flags.has('legacy.auth.enabled')" />
              <div
                v-else
                class="absolute inset-x-0 -bottom-6 me-auto ms-auto w-[320px]"
              >
                <div class="bg-neutral-50 text-center">
                  <KikButton
                    class="h-[48px] min-w-[280px]"
                    @click="signInAndGotToCheckout"
                  >
                    {{ $t('Sign in') }}
                  </KikButton>
                </div>
              </div>
            </div>
          </section>
          <section
            v-if="$flags.has('legacy.auth.enabled')"
            class="px-4 py-1"
          >
            <div class="relative mb-8 grid gap-2 border-4 border-neutral-50 p-6 text-center">
              <SignMeUp @start-registration="startRegistration" />
            </div>
          </section>
          <section
            v-if="loginAuthType === 'default'"
            class="px-4 py-1"
          >
            <CheckGuestOrder />
          </section>
          <section
            v-if="loginAuthType === 'checkout'"
            class="px-4 py-2"
          >
            <OrderAsGuest />
          </section>
          <section
            class="px-4 py-1"
          >
            <CanWeHelp />
          </section>
        </template>
        <div
          v-else
          class="p-4"
        >
          <Register v-if="$flags.has('legacy.auth.enabled')" />
        </div>
      </div>
      <div
        v-if="viewType === 'registration'"
        class="mr-4 p-4"
      >
        <KikButton
          class="w-1/2 !bg-neutral-900"
          @click="viewType = 'default'"
        >
          {{ $t('Cancel') }}
        </KikButton>
      </div>

      <footer
        v-else-if="viewType === 'default'"
        class="mt-auto border-t p-4"
      >
        <KikButton
          class="w-full"
          variant="secondary"
          @click="toggleLoginDrawer"
        >
          {{ $t('Close') }}
        </KikButton>
      </footer>
    </SfDrawer>
  </transition>
</template>

<script setup lang="ts">
import { SfDrawer, SfIconClose } from '@storefront-ui/vue';
import Register from '~/components/AuthDrawer/Register.vue';
import LegacyAuth from '~/components/AuthDrawer/Legacy/index.vue';
import SignMeUp from '~/components/AuthDrawer/fragments/SignMeUp.vue';
import CheckGuestOrder from '~/components/AuthDrawer/fragments/CheckGuestOrder.vue';
import { useUser } from '~/composables/custom/useUser/useUser';
import OrderAsGuest from '~/components/AuthDrawer/fragments/OrderAsGuest.vue';
import CanWeHelp from '~/components/AuthDrawer/fragments/CanWeHelp.vue';
import { useCheckoutNavigation, UserType } from '~/composables/custom/useCheckoutNavigation/useCheckoutNavigation';
import useUiState from '~/composables/useUiState';

type ViewType = 'default' | 'registration';

const viewType = ref<ViewType>('default');

const { $flags } = useNuxtApp();
const { signIn } = useUser();
const { journey: { setUserType } } = useCheckoutNavigation();
const { isLoginDrawerOpen, toggleLoginDrawer, loginAuthType, closeLoginDrawer } = useUiState();

const startRegistration = () => {
  if (loginAuthType.value === 'default') {
    viewType.value = 'registration';

    return;
  }

  setUserType(UserType.NEW);
  closeLoginDrawer();
  navigateTo({ name: 'billing-new-customer', query: { newCustomer: 'true' } });
};

const signInAndGotToCheckout = async () => {
  try {
    const redirectTo = window.location.pathname || '/';
    await signIn(redirectTo);
    setUserType(UserType.AUTHENTICATED);
    closeLoginDrawer();
    navigateTo('/checkout/billing-customer');
  }
  catch (error) {
    console.error('Error during sign in:', error);
  }
};
</script>
